import React, { type ReactNode } from 'react'
import { type LinkProps, useLocation } from 'react-router-dom'

import { useDashboardFeatureFlags } from '@components'
import { Dashboard, PageLayout } from '@components/common'
import { useAppearance } from '@fourthwall/appearance'
import {
  BackButton,
  Banner,
  type BannerProps,
  Button,
  ConfirmModal,
  HeadContent,
  Icon,
  useMediaQueryContext,
  useModalContext,
} from '@fourthwall/components'
import { DownloadIcon } from '@fourthwall/icons'
import {
  AnalyticsBarChart,
  type AnalyticsBarChartProps,
  AnalyticsCard,
  AnalyticsChart,
  StatsTable,
} from '@fourthwall/module-analytics'
import { isWebView } from '@fourthwall/utils'
import type { LocationState } from '@types'

import { AnalyticsFiltersButton } from '../../../../modules/AnalyticsFilters/components/AnalyticsFiltersButton/AnalyticsFiltersButton'
import { useAnalyticsFilters } from '../../../../modules/AnalyticsFilters/contexts/AnalyticsFiltersContext'
import { removeFiltersParams } from '../../../../modules/AnalyticsFilters/utils/removeFiltersParams'
import {
  AnalyticsToolbar,
  ChartEntityGroupSelect,
  DevicesSelect,
  IntervalSelect,
  PrecisionSelect,
  RangeSelect,
  TiersSelect,
} from '../../../../modules/AnalyticsParams'
import * as S from './AnalyticsReport.styled'
import type { AnalyticsReportChart, AnalyticsReportStatsTable } from './AnalyticsReport.types'

export type AnalyticsReportViewProps = {
  title: string
  bottomContent?: ReactNode
  banner?: BannerProps
  backButtonLabel: string
  backButtonTo: LinkProps['to']
  exportButton: { enabled: boolean; onConfirm: () => void }
  chart?: AnalyticsReportChart
  analyticsBarChart: Omit<AnalyticsBarChartProps, 'emptyMessage'> | undefined
  statsTable: AnalyticsReportStatsTable<any>
}

export const AnalyticsReportView = ({
  title,
  bottomContent,
  banner,
  backButtonLabel,
  backButtonTo,
  exportButton,
  chart,
  analyticsBarChart,
  statsTable,
}: AnalyticsReportViewProps) => {
  const { filters, hasActiveFilters, onChange } = useAnalyticsFilters()
  const { appearance } = useAppearance()
  const media = useMediaQueryContext()
  const { open } = useModalContext()
  const location = useLocation()
  const locationState = location.state as LocationState
  const { dashboardFeatureFlags } = useDashboardFeatureFlags()

  const backButtonProps = (() => {
    if (locationState?.from) {
      const searchParams = new URLSearchParams(locationState.from.location.search)
      const cleanedParams = removeFiltersParams(searchParams)

      return {
        label: locationState.from.label,
        to: {
          pathname: locationState.from.location.pathname,
          search: cleanedParams.toString(),
        },
      }
    }

    return {
      label: backButtonLabel,
      to: backButtonTo,
    }
  })()

  const handleExportClick = () => {
    open(ConfirmModal, {
      title: 'Export report',
      text: 'Report will be exported as CSV table.',
      confirmLabel: 'Export',
      onConfirm: exportButton.onConfirm,
    })
  }

  const renderStatsTable = () => {
    if (analyticsBarChart && !media.tablet) {
      return (
        <AnalyticsBarChart
          {...analyticsBarChart}
          emptyMessage="No data found in this date range."
        />
      )
    }

    return (
      <StatsTable
        columns={statsTable.columns}
        data={statsTable.data}
        emptyMessage={statsTable.emptyMessage}
        isLoading={statsTable.isLoading}
        getRowBold={statsTable.getRowBold}
      />
    )
  }

  return (
    <>
      <HeadContent title={title} titleTemplate={isWebView() ? title : undefined} />
      <PageLayout size={dashboardFeatureFlags.analyticsTabs.enabled ? 'full' : undefined}>
        <Dashboard.Section
          name={isWebView() ? undefined : title}
          topContent={isWebView() ? undefined : <BackButton {...backButtonProps} />}
          bottomContent={bottomContent}
        >
          <AnalyticsToolbar
            actions={
              <>
                {isWebView() || !exportButton.enabled ? undefined : (
                  <Button
                    leftIcon={<Icon component={DownloadIcon} height={18} />}
                    label="Export"
                    appearance="secondary"
                    size={media.tablet ? 'medium' : 'small'}
                    onClick={handleExportClick}
                  />
                )}
                {dashboardFeatureFlags.analyticsTabs.enabled && (
                  <AnalyticsFiltersButton
                    filters={filters}
                    isActive={hasActiveFilters}
                    onChange={onChange}
                  />
                )}
              </>
            }
          >
            <RangeSelect excludedPrecision={['WEEK', 'MONTH', 'YEAR']} />
            <PrecisionSelect excludedPrecision={['WEEK', 'MONTH', 'YEAR']} />
            {!dashboardFeatureFlags.analyticsTabs.enabled && (
              <>
                <ChartEntityGroupSelect />
                <TiersSelect />
                <IntervalSelect />
                <DevicesSelect />
              </>
            )}
          </AnalyticsToolbar>

          <S.ContentContainer>
            {banner && <Banner {...banner} />}
            {chart && (
              <AnalyticsCard
                title={chart.title}
                value={chart.value}
                statistics={
                  <AnalyticsChart
                    description={chart.description}
                    series={chart.series}
                    formatDate={chart.formatDate}
                    formatValue={chart.formatValue}
                    minSecondaryAxesValue={chart.minSecondaryAxesValue}
                    appearance={appearance}
                  />
                }
                isLoading={chart.isLoading}
                isError={chart.isError}
              />
            )}
            {renderStatsTable()}
          </S.ContentContainer>
        </Dashboard.Section>
      </PageLayout>
    </>
  )
}
